import { Button, CircularProgress, Container, Dialog, DialogActions, DialogContent, DialogTitle, FormControl, InputLabel, MenuItem, Select, Stack, TextField, Typography } from '@mui/material';
import axios from 'axios';
import { useSnackbar } from 'notistack';
import { useEffect, useState } from 'react';
import MatterForm from '../sections/@dashboard/matter/MatterForm';
import MatterList from '../sections/@dashboard/matter/MatterList';

const SORT_OPTIONS = [
  { value: 'caseNumberAsc', label: 'Case Number: Ascending' },
  { value: 'caseNumberDesc', label: 'Case Number: Descending' },
  { value: 'dateCreatedDesc', label: 'Date Created: Newest First' },
  { value: 'clientAsc', label: 'Client: A-Z' },
  { value: 'statusAsc', label: 'Status: A-Z' },
];

export default function Matters() {
  const [matters, setMatters] = useState([]);
  const [searchTerm, setSearchTerm] = useState('');
  const [openForm, setOpenForm] = useState(false);
  const [sortBy, setSortBy] = useState('dateCreatedDesc');
  const [loading, setLoading] = useState(true);
  const [estateNo, setEstateNo] = useState('');
  const [currentPage, setCurrentPage] = useState(1);
  const [openDialog, setOpenDialog] = useState(false);
  const [matterToDelete, setMatterToDelete] = useState(null);
  const { enqueueSnackbar } = useSnackbar();
  const [user, setUser] = useState(null);
  const [searchQuery, setSearchQuery] = useState('');

  const isAdmin = user?.role === 'admin';

  useEffect(() => {
    const fetchUser = async () => {
      try {
        const res = await axios.get('https://ipms.insollex.com/api/user', { withCredentials: true });
        setUser(res.data);
      } catch (err) {
        console.error('Error fetching user:', err);
      }
    };
  
    fetchUser();
  }, []);


  useEffect(() => {
    const fetchMatters = async () => {
      if (!user) return;
  
      setLoading(true);
  
      try {
        // 👤 Guest/invited users → get all invited matters
        if (user.role === 'guest' || user.isInvited) {
          const response = await axios.get(`https://ipms.insollex.com/api/legal-matter/invited/${user.emailAddress}`);
          const invitedMatters = Array.isArray(response.data) 
            ? response.data 
            : (response.data?.matter ? [response.data.matter] : [response.data]);
  
          if (invitedMatters && invitedMatters.length > 0) {
            setMatters(invitedMatters);
          } else {
            enqueueSnackbar('No invited matters found', { variant: 'warning' });
          }
  
          return;
        }
  
        // 👨‍⚖️ Regular/admin users → all matters
        const [legalMattersResponse, matterCardsResponse] = await Promise.allSettled([
          axios.get('https://ipms.insollex.com/api/legal-matter'),
          axios.get('https://ipms.insollex.com/api/matter-card')
        ]);
  
        const legalMatters = legalMattersResponse.status === 'fulfilled' 
          ? legalMattersResponse.value.data?.matters || [] 
          : [];
  
        const matterCards = matterCardsResponse.status === 'fulfilled' 
          ? matterCardsResponse.value.data || [] 
          : [];
  
        const combinedMatters = [...legalMatters, ...matterCards];
  
        if (combinedMatters.length === 0) {
          enqueueSnackbar('No matters found', { variant: 'warning' });
        }
  
        setMatters(combinedMatters);
      } catch (error) {
        console.error('Error fetching matters:', error);
        enqueueSnackbar('Failed to fetch matters', { variant: 'error' });
      } finally {
        setLoading(false);
      }
    };
  
    fetchMatters();
  }, [enqueueSnackbar, user]);

  const handleSearchChange = (event) => {
    setSearchTerm(event.target.value);
  };

  const handleSortChange = (event) => {
    setSortBy(event.target.value);
  };

  const handleDeleteMatter = async () => {
    try {
        const relatedFilesResponse = await axios.get(`https://ipms.insollex.com/api/legal-file?legalMatterId=${matterToDelete}`);
        
        if (relatedFilesResponse.data.length > 0) {
            enqueueSnackbar('Cannot delete matter with related documents', { variant: 'error' });
            return;
        }

        await axios.delete(`https://ipms.insollex.com/api/legal-matter/${matterToDelete}`);
        setMatters(matters.filter((matter) => matter.id !== matterToDelete));
        enqueueSnackbar('Matter deleted successfully', { variant: 'success' });

    } catch (error) {
        if (error.response) {
            console.error('Error deleting matter:', error.response.data);
            enqueueSnackbar('Failed to delete matter: ' + error.response.data.message, { variant: 'error' });
        } else {
            enqueueSnackbar('An unexpected error occurred', { variant: 'error' });
        }
    } finally {
        setOpenDialog(false);
        setMatterToDelete(null);
    }
};

const handleUpdateStatus = async (id, updatedStatus) => {
  try {
    const response = await axios.patch(`https://ipms.insollex.com/api/legal-matter/${id}/status`, { status: updatedStatus });

    // Update local state
    setMatters((prevMatters) =>
      prevMatters.map((matter) =>
        matter.id === id ? { ...matter, status: updatedStatus } : matter
      )
    );

    enqueueSnackbar('Status updated successfully', { variant: 'success' });
  } catch (error) {
    console.error('Error updating status:', error);
    enqueueSnackbar('Failed to update status', { variant: 'error' });
  }
};



  const openDeleteDialog = (matterId) => {
    setMatterToDelete(matterId);
    setOpenDialog(true);
  };

  const handleCloseDialog = () => {
    setOpenDialog(false);
    setMatterToDelete(null);
  };

  const handleAddMatter = (newMatter) => {
    setMatters((prev) => [newMatter, ...prev]);
    setEstateNo(newMatter.estateNo);
    setOpenForm(false);
    enqueueSnackbar('Matter added successfully', { variant: 'success' });
  };

  // Custom sorting logic
  const statusOrder = { 'In Progress': 1, 'Pending': 2, 'Closed': 3 };

  const filteredMatters = (searchTerm.trim() === '')
  ? []
  : (matters || []).filter((matter) => {
      const term = searchTerm.toLowerCase();
      return (
        matter?.matterName?.toLowerCase().includes(term) ||
        matter?.estateNo?.toLowerCase?.().includes(term) ||
        matter?.capturedBy?.toLowerCase?.().includes(term) ||
        matter?.client?.toLowerCase?.().includes(term) ||
        matter?.trusteeReference?.toLowerCase?.().includes(term) ||
        matter?.idNumber?.toLowerCase?.().includes(term) ||
        matter?.phoneNumber?.toLowerCase?.().includes(term) ||
        matter?.registrationNumber?.toLowerCase?.().includes(term) // ✅ Added field
      );
    })
  .sort((a, b) => {
      switch (sortBy) {
        case 'caseNumberAsc':
          return a.caseNumber - b.caseNumber;
        case 'caseNumberDesc':
          return b.caseNumber - a.caseNumber;
        case 'dateCreatedAsc':
          return new Date(a.createdAt) - new Date(b.createdAt);
        case 'dateCreatedDesc':
          return new Date(b.createdAt) - new Date(a.createdAt);
        case 'clientAsc':
          return a.client?.localeCompare(b.client) || 0;
        case 'clientDesc':
          return b.client?.localeCompare(a.client) || 0;
        case 'statusAsc':
          return statusOrder[a.status] - statusOrder[b.status];
        case 'statusDesc':
          return statusOrder[b.status] - statusOrder[a.status];
        default:
          return 0;
      }
    });

    const normalizedMatters = filteredMatters.length > 0
    ? filteredMatters.sort((a, b) => {
        if (sortBy !== 'statusAsc' && sortBy !== 'statusDesc') {
          const statusA = a.status?.trim().replace(/\b\w/g, (char) => char.toUpperCase());
          const statusB = b.status?.trim().replace(/\b\w/g, (char) => char.toUpperCase());
          if (statusA && statusB && statusOrder[statusA] !== statusOrder[statusB]) {
            return statusOrder[statusA] - statusOrder[statusB];
          }
        }
        return 0;
      })
    : [];

    
  // Pagination logic
const pageSize = 16;
const paginatedMatters = normalizedMatters.slice(
  (currentPage - 1) * pageSize,
  currentPage * pageSize
);

const totalPages = Math.ceil(normalizedMatters.length / pageSize);

// Function to generate visible page numbers
const getVisiblePageNumbers = () => {
  const maxPagesToShow = 5; // Limit number of pages shown
  const halfRange = Math.floor(maxPagesToShow / 2);
  let start = Math.max(1, currentPage - halfRange);
  let end = Math.min(totalPages, start + maxPagesToShow - 1);

  if (end === totalPages) {
    start = Math.max(1, end - maxPagesToShow + 1);
  }

  let pages = [];
  if (start > 1) {
    pages.push(1);
    if (start > 2) pages.push('...');
  }

  for (let i = start; i <= end; i++) {
    pages.push(i);
  }

  if (end < totalPages) {
    if (end < totalPages - 1) pages.push('...');
    pages.push(totalPages);
  }

  return pages;
};


  const statusTooltips = {
    'in progress': 'The matter is currently being worked on.',
    'pending': 'The matter is awaiting further action.',
    'closed': 'The matter has been resolved or closed.',
  };

  return (
    <Container maxWidth={false} aria-hidden={openDialog ? true : false}>
      <Typography variant="h6" sx={{ mb: 5 }}>
        Matters
      </Typography>
  
      <Stack direction="row" justifyContent="space-between" sx={{ mb: 3 }}>
        <TextField
          label="Search Matters"
          variant="outlined"
          value={searchTerm}
          onChange={handleSearchChange}
          sx={{ width: '600px' }}
        />
        <FormControl sx={{ width: '400px' }}>
          <InputLabel id="sort-by-label">Sort By</InputLabel>
          <Select
            labelId="sort-by-label"
            value={sortBy}
            label="Sort By"
            onChange={handleSortChange}
          >
            {SORT_OPTIONS.map((option) => (
              <MenuItem key={option.value} value={option.value}>
                {option.label}
              </MenuItem>
            ))}
          </Select>
        </FormControl>
  
        <Button
          variant="contained"
          color="primary"
          onClick={() => setOpenForm(true)}
          disabled={user?.role === 'guest' || user?.isInvited}
        >
          Add Matter
        </Button>
      </Stack>
  
      {openForm && (
        <MatterForm
          openForm={openForm}
          setOpenForm={setOpenForm}
          onAddLegalMatter={handleAddMatter} // Pass the function to the form
        />
      )}
  
      {loading && (
        <CircularProgress sx={{ display: 'block', margin: 'auto' }} />
      )}
  
      <MatterList
      matters={paginatedMatters}
      onDeleteMatter={openDeleteDialog}
      isAdmin={isAdmin}
      statusTooltips={statusTooltips}
      setMatters={setMatters} // ✅ Pass the missing prop
      />
  
      {/* Pagination Controls */}
      <Stack direction="row" justifyContent="center" sx={{ mt: 3 }}>
  <Button
    variant="outlined"
    onClick={() => setCurrentPage(1)}
    disabled={currentPage === 1}
  >
    {'<<'}
  </Button>
  <Button
    variant="outlined"
    onClick={() => setCurrentPage(currentPage - 1)}
    disabled={currentPage === 1}
  >
    {'<'}
  </Button>

  {getVisiblePageNumbers().map((pageNum, index) => (
    <Button
      key={index}
      variant={pageNum === currentPage ? 'contained' : 'outlined'}
      onClick={() => typeof pageNum === 'number' && setCurrentPage(pageNum)}
      disabled={typeof pageNum !== 'number'}
      sx={{ mx: 0.5 }}
    >
      {pageNum}
    </Button>
  ))}

  <Button
    variant="outlined"
    onClick={() => setCurrentPage(currentPage + 1)}
    disabled={currentPage === totalPages}
  >
    {'>'}
  </Button>
  <Button
    variant="outlined"
    onClick={() => setCurrentPage(totalPages)}
    disabled={currentPage === totalPages}
  >
    {'>>'}
  </Button>
</Stack>
      <Typography sx={{ mt: 2, textAlign: 'center' }}>
        Page {currentPage} of {totalPages}
      </Typography>
  
      {/* Confirmation Dialog for Deletion */}
      <Dialog open={openDialog} onClose={handleCloseDialog}>
        <DialogTitle>Confirm Deletion</DialogTitle>
        <DialogContent>
          <Typography>Are you sure you want to delete this matter?</Typography>
        </DialogContent>
        <DialogActions>
          <Button onClick={handleCloseDialog} color="primary">
            Cancel
          </Button>
          <Button onClick={handleDeleteMatter} color="error">
            Delete
          </Button>
        </DialogActions>
      </Dialog>
    </Container>
  );  
}
